import PrescriptionPrintTyped from "./PrescriptionPrintTyped";
import PrescriptionPrintHandwritten from "./PrescriptionPrintHandwritten";

const MM_TO_PX = 3.7795275591;
const mm = (val) => (val || 0) * MM_TO_PX;

/*
  Layout constants — all in px at 96dpi
  A4 = 210mm × 297mm = 793.7px × 1122.5px
*/
const A4_W_PX     = 793.7;
const A4_H_PX     = 1122.5;
const PAGE_PAD    = 24;   // left/right padding px
const HEADER_H_PX = 155;  // logo (bigger) + English + Devanagari + doctor text + rules
const PATIENT_H_PX= 32;   // height of patient info row
const PATIENT_GAP = 6;    // gap after patient row

/* ── Compute layout positions ── */
function layout(s) {
  const topOff     = mm(s?.topOffset    || 0);
  const footerOff  = mm(s?.footerOffset || 0);
  const hasHeader  = !!s?.showConsultantHeader;
  const hasFooter  = !!s?.showFooter;

  // Compute actual header height based on content lines and font size
  let actualHeaderH = 0;
  if (hasHeader && s?.headerBox?.content) {
    const lines     = (s.headerBox.content.split("<br/>").length) || 1;
    const fontSize  = s.headerBox?.style?.fontSize  || 18;
    const lineH     = s.headerBox?.style?.lineHeight || 1.5;
    const paraSpace = s.headerBox?.style?.paragraphSpacing || 8;
    actualHeaderH   = Math.max(HEADER_H_PX, lines * fontSize * lineH + (lines - 1) * paraSpace + 14);
  } else if (hasHeader) {
    actualHeaderH = HEADER_H_PX;
  }

  const headerTop  = topOff;
  const patientTop = topOff + actualHeaderH;             // patient row starts after real header height
  const bodyTop    = patientTop + PATIENT_H_PX + PATIENT_GAP;
  const footerBot  = footerOff;
  const hasStampImg = !!s?.doctorStamp?.signatureImageUrl;
  const footerReserve = hasFooter ? (hasStampImg ? 78 : 52) : 0;
  const bodyBottom = footerOff + footerReserve + 4;

  const bodyHeight = Math.max(200, A4_H_PX - bodyTop - bodyBottom);

  return { headerTop, patientTop, bodyTop, bodyBottom, footerBot, bodyHeight, actualHeaderH };
}

/* ── Clinic brand colours (match herambaayurveda.in) ── */
const BRAND = {
  green:    "#2D5A3D",
  greenMid: "#4A7C59",
  gold:     "#B86A10",
  goldL:    "#E8922A",
  terra:    "#B85C38",
  copper:   "#C17A3A",
  ink:      "#2C1810",
};

/* ── Header ── */
function PrintHeader({ s, L }) {
  if (!s?.showConsultantHeader) return null;
  // NOTE: previously this bailed out completely (returning null) whenever
  // the doctor hadn't typed custom header text (hb.content empty) — which
  // meant the clinic logo/name/branding never printed at all even though
  // "Show header" was switched on. The fixed brand block below should
  // always print when the toggle is on; only the optional custom text
  // line is conditional on having content.
  const hb = s.headerBox || {};

  const logoSrc  = "/logo.png";
  const fontSize = hb.style?.fontSize || 18;

  return (
    <div
      id="rx-print-header"
      className="rx-print-header a4-page-header"
      style={{
      display:       "block",
      position:      "absolute",
      top:           L.headerTop + "px",
      left:          PAGE_PAD + "px",
      right:         PAGE_PAD + "px",
      minHeight:     (L.actualHeaderH || HEADER_H_PX) + "px",
      boxSizing:     "border-box",
      paddingBottom: "8px",
    }}>

      {/* ── Top green-to-gold accent bar ── */}
      <div style={{
        height:       4,
        background:   `linear-gradient(90deg, ${BRAND.green} 0%, ${BRAND.goldL} 55%, ${BRAND.green} 100%)`,
        borderRadius: "2px 2px 0 0",
        marginBottom: 8,
      }} />

      {/* ── Logo (absolute left) + text (centred) ── */}
      <div style={{
        position: "relative",
        width:    "100%",
        display:  "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>

        {/* Logo — pinned to left, vertically centred */}
        <img
          src={logoSrc}
          alt="Heramba Ayurveda"
          style={{
            position:   "absolute",
            left:       0,
            top:        "50%",
            transform:  "translateY(-50%)",
            height:     Math.max(72, fontSize * 4) + "px",
            width:      "auto",
            objectFit:  "contain",
            display:    "block",
          }}
          onError={e => { e.target.style.display = "none"; }}
        />

        {/* Name block — centred in the full width */}
        <div style={{ textAlign: "center" }}>

          {/* English — Playfair Display, dark green */}
          <div style={{
            fontFamily:  "'Playfair Display', serif",
            fontSize:    (fontSize + 2) + "px",
            fontWeight:  800,
            color:       BRAND.green,
            lineHeight:  1.2,
            letterSpacing: "0.03em",
          }}>
            Heramba Ayurveda
          </div>

          {/* Devanagari — Amita Bold, gold gradient, with side lines */}
          <div style={{
            display:        "flex",
            alignItems:     "center",
            justifyContent: "center",
            gap:            10,
            margin:         "3px 0 2px",
          }}>
            <div style={{
              height:     1.5,
              width:      40,
              background: `linear-gradient(90deg, transparent, ${BRAND.goldL}90)`,
            }} />
            <span style={{
              fontFamily:              "'Amita', serif",
              fontWeight:              700,
              fontSize:                (fontSize + 4) + "px",
              background:              `linear-gradient(135deg, ${BRAND.goldL} 0%, ${BRAND.terra} 45%, ${BRAND.copper} 100%)`,
              WebkitBackgroundClip:    "text",
              WebkitTextFillColor:     "transparent",
              backgroundClip:          "text",
              letterSpacing:           "0.06em",
              lineHeight:              1.4,
              display:                 "inline-block",
            }}>
              हेरम्ब आयुर्वेद
            </span>
            <div style={{
              height:     1.5,
              width:      40,
              background: `linear-gradient(90deg, ${BRAND.goldL}90, transparent)`,
            }} />
          </div>

          {/* Doctor / clinic text from header box (optional — only
              rendered when the doctor has actually typed something) */}
          {hb.content && hb.content.trim() && (
            <div style={{
              fontFamily:     hb.style?.fontFamily  || "Times New Roman",
              fontSize:       (fontSize - 1) + "px",
              lineHeight:     hb.style?.lineHeight  || 1.5,
              textAlign:      "center",
              fontWeight:     hb.tools?.bold        ? "700" : "400",
              fontStyle:      hb.tools?.italic      ? "italic" : "normal",
              textDecoration: hb.tools?.underline   ? "underline" : "none",
              color:          BRAND.ink,
              marginTop:      2,
            }}>
              {hb.content.split("<br/>").map((line, i) => (
                <div key={i}>{line || "\u00A0"}</div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Gold shimmer rule ── */}
      <div style={{
        height:     1.5,
        background: `linear-gradient(90deg, transparent, ${BRAND.goldL}, ${BRAND.terra}, ${BRAND.goldL}, transparent)`,
        marginTop:  7,
      }} />

      {/* ── Green bottom border ── */}
      <div style={{
        height:     1,
        background: BRAND.greenMid,
        marginTop:  3,
      }} />
    </div>
  );
}

/* ── Patient row ── */
function PatientRow({ rx, L }) {
  const p = rx?.patient || {};
  return (
    <div
      id="rx-print-patient"
      className="rx-print-patient a4-page-patient"
      style={{
      position:       "absolute",
      top:            L.patientTop + "px",
      left:           PAGE_PAD + "px",
      right:          PAGE_PAD + "px",
      height:         PATIENT_H_PX + "px",
      display:        "flex",
      justifyContent: "space-between",
      alignItems:     "center",
      fontSize:       "11.5px",
      borderBottom:   "1px solid #e5e7eb",
      boxSizing:      "border-box",
    }}>
      <span><b>Name:</b> {p.name    || "—"}</span>
      <span><b>Age:</b>  {p.age     || "—"}</span>
      <span><b>Gender:</b>{p.gender || "—"}</span>
      <span><b>Date:</b>  {rx?.createdAt
        ? new Date(rx.createdAt).toLocaleDateString("en-IN")
        : new Date().toLocaleDateString("en-IN")}</span>
      <span><b>Rx#:</b>  {rx?.prescriptionId || "—"}</span>
    </div>
  );
}

/* ── Footer ── */
function PrintFooter({ s, L, rx }) {
  if (!s?.showFooter) return null;
  // Same reasoning as the header: don't bail out to nothing just because
  // the doctor hasn't typed footer text yet — still render the footer
  // rule/space when "Show footer" is switched on, so the printable area
  // always reserves and shows the footer band.
  const fb = s.footerBox || {};
  const stamp = s.doctorStamp || {};
  const hasStamp = !!(stamp.name || stamp.registrationNo || stamp.signatureImageUrl);
  const visitDate = rx?.createdAt
    ? new Date(rx.createdAt).toLocaleDateString("en-IN")
    : new Date().toLocaleDateString("en-IN");

  return (
    <div
      id="rx-print-footer"
      className="rx-print-footer a4-page-footer"
      style={{
      display:        "flex",
      alignItems:     "flex-end",
      justifyContent: "space-between",
      gap:            12,
      position:       "absolute",
      bottom:         L.footerBot + "px",
      left:           PAGE_PAD + "px",
      right:          PAGE_PAD + "px",
      borderTop:      "1px solid #d1d5db",
      paddingTop:     "6px",
      minHeight:      "14px",
      background:     "#fff",
      boxSizing:      "border-box",
    }}>

      {/* ── Doctor Stamp (filled once in Prescription Settings, reused
          on every print) — signature/stamp image + name + reg. no.,
          next to THIS prescription's own visit date. ── */}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 8, fontFamily: "Times New Roman" }}>
        {hasStamp ? (
          <>
            {stamp.signatureImageUrl && (
              <img
                src={stamp.signatureImageUrl}
                alt="Doctor signature"
                crossOrigin="anonymous"
                style={{ height: 34, maxWidth: 110, objectFit: "contain", display: "block" }}
              />
            )}
            <div style={{ fontSize: 11, lineHeight: 1.35 }}>
              {stamp.name && <div style={{ fontWeight: 700, color: BRAND.ink }}>{stamp.name}</div>}
              {stamp.registrationNo && <div style={{ color: "#4b5563" }}>Reg. No: {stamp.registrationNo}</div>}
              <div style={{ color: "#6b7280", fontSize: 10 }}>Date: {visitDate}</div>
            </div>
          </>
        ) : <div />}
      </div>

      {/* ── Existing footer text (unchanged) ── */}
      <div style={{
        fontFamily:     fb.style?.fontFamily  || "Times New Roman",
        fontSize:       (fb.style?.fontSize   || 14) + "px",
        lineHeight:     fb.style?.lineHeight  || 1.5,
        textAlign:      fb.style?.textAlign   || "right",
        fontWeight:     fb.tools?.bold        ? "700" : "400",
        fontStyle:      fb.tools?.italic      ? "italic" : "normal",
        textDecoration: fb.tools?.underline   ? "underline" : "none",
      }}>
        {fb.content && fb.content.trim()
          ? fb.content.split("<br/>").map((line, i) => (
              <div key={i}>{line || "\u00A0"}</div>
            ))
          : <div>&nbsp;</div>}
      </div>
    </div>
  );
}

/* ── A4 page wrapper ── */
function A4Page({ s, rx, children }) {
  const L = layout(s);

  return (
    /*
      CRITICAL FOR PRINT:
      - width + height must both be explicit so browser knows page size
      - overflow:hidden clips content to A4 bounds
      - display:block ensures the div has physical height in print flow
      - page-break-after for multi-page
    */
    <div className="rx-print-page" id="rx-print-page" style={{
      position:       "relative",
      display:        "block",
      width:          "210mm",
      height:         "297mm",
      background:     "#fff",
      overflow:       "hidden",
      pageBreakAfter: "always",
      breakAfter:     "page",
      boxSizing:      "border-box",
    }}>
      <PrintHeader s={s} L={L} />
      <PatientRow  rx={rx} L={L} />

      {/* Body — anchored top+bottom, no height set so it doesn't clip footer */}
      <div
        id="rx-print-body"
        className="rx-print-body"
        style={{
        display:  "block",
        position: "absolute",
        top:      L.bodyTop    + "px",
        bottom:   L.bodyBottom + "px",
        left:     PAGE_PAD + "px",
        right:    PAGE_PAD + "px",
        overflow: "hidden",
      }}>
        {children}
      </div>

      <PrintFooter s={s} L={L} rx={rx} />
    </div>
  );
}

/* ── Main export ── */
export default function PrescriptionPrintCombined({ rx }) {
  if (!rx) return null;

  const s = rx?.settings || {};
  const L = layout(s);

  // Determine what to render — fall back gracefully if modeUsed is missing/empty
  const hasTyped       = rx.modeUsed?.includes("typed")       || (!rx.modeUsed?.length && rx.typed && Object.values(rx.typed).some(v => v?.trim()));
  const hasHandwritten = rx.modeUsed?.includes("handwritten") || (!rx.modeUsed?.length && rx.handwritten?.image);

  // Nothing at all — show a simple fallback so the modal isn't blank
  if (!hasTyped && !hasHandwritten) {
    return (
      <div style={{
        width: "210mm", minHeight: "297mm", background: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "#9ca3af", fontSize: "14px", fontFamily: "sans-serif"
      }}>
        No prescription content saved.
      </div>
    );
  }

  return (
    <div id="print-area">
      {/* TYPED prescription */}
      {hasTyped && (
        <PrescriptionPrintTyped rx={rx} usableBodyHeight={L.bodyHeight}>
          {(pages) =>
            pages.map((p, i) => (
              <A4Page key={`typed-${i}`} s={s} rx={rx}>
                <div
                  style={{ width: "100%" }}
                  dangerouslySetInnerHTML={{ __html: p.html }}
                />
              </A4Page>
            ))
          }
        </PrescriptionPrintTyped>
      )}

      {/* HANDWRITTEN prescription */}
      {hasHandwritten && (
        <PrescriptionPrintHandwritten rx={rx}>
          {(pages) =>
            pages.map((p, i) => (
              <A4Page key={`hw-${i}`} s={s} rx={rx}>
                <div style={{
                  width: "100%", height: "100%",
                  display: "flex", alignItems: "flex-start", justifyContent: "center",
                }}>
                  <img
                    src={p.image}
                    crossOrigin="anonymous"
                    style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain", display: "block" }}
                    alt="Handwritten prescription"
                  />
                </div>
              </A4Page>
            ))
          }
        </PrescriptionPrintHandwritten>
      )}
    </div>
  );
}